import React, { useState } from "react";
import { Button, Dropdown, Icon, Modal } from "semantic-ui-react";

import type { UserClientAssociation } from "../../../../apis/types/trialTypes";

import styles from "./SelectUserClientModal.module.css";

interface SelectUserClientModalProps {
	open: boolean;
	/** `"Owner"` or `"Sponsor"` -- what the chosen organization is being assigned as. */
	assignmentType: string;
	emailAddress: string;
	/** The assignee's name, blank when the API did not supply one. */
	userName: string;
	clients: UserClientAssociation[];
	saving: boolean;
	onCancel: () => void;
	onConfirm: (client: UserClientAssociation) => void;
}

/**
 * Asks which of a user's organizations a trial should be assigned to. Shown only when the user
 * belongs to more than one; a single organization is assigned without prompting.
 */
const SelectUserClientModal = ({
	open,
	assignmentType,
	emailAddress,
	userName,
	clients,
	saving,
	onCancel,
	onConfirm,
}: SelectUserClientModalProps) => {
	//-- The modal is reused for the owner and for each sponsor; the parent keys it per assignment so
	//-- each prompt starts unanswered.
	const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

	const clientOptions = clients.map((c) => ({ key: c.clientId, value: c.clientId, text: c.clientName }));
	const selectedClient = clients.find((c) => c.clientId === selectedClientId) ?? null;

	return (
		<Modal open={open} onClose={onCancel} size="tiny">
			<Modal.Header>
				Choose an Organization
				<Icon name="close" link onClick={onCancel} />
			</Modal.Header>
			<Modal.Content>
				<div className={styles.description}>
					{userName ? `${userName} ` : ""}
					<span className={styles.emailAddress}>{emailAddress}</span> belongs to more than one organization. Choose
					which one to assign as the trial {assignmentType.toLowerCase()}.
				</div>
				<Dropdown
					id="select-user-client"
					className={styles.dropdown}
					selection
					search
					options={clientOptions}
					value={selectedClientId ?? ""}
					onChange={(event, { value }) => setSelectedClientId(value as string)}
					placeholder="Organization"
					disabled={saving}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button type="button" onClick={onCancel} disabled={saving}>
					Cancel
				</Button>
				<Button
					type="button"
					positive
					loading={saving}
					disabled={saving || !selectedClient}
					onClick={() => {
						if (selectedClient) onConfirm(selectedClient);
					}}
				>
					Assign
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default SelectUserClientModal;
